import { useState, useEffect } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { CareerTrackRadio } from '@backrs_ui/common-ui/src/atoms/CareerTrackRadio';
import { CriteriaText } from '@backrs_ui/common-ui/src/atoms/CriteriaText';
import { DimensionScorePill } from '@backrs_ui/common-ui/src/atoms/DimensionScorePill';
import { InfoModal } from '@backrs_ui/common-ui/src/atoms/InfoModal';
import {
  CRITERIA_LEVELS,
  CRITERIA_LEVEL_SCORES,
  CriteriaLevel,
  Standard,
  StandardsVariant,
  TrackAssessmentCategory,
  TrackAssessmentDimCriteria,
} from '@backrs_ui/common-ui/src/models/trackAssessment';

import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  SxProps,
  Theme,
  useMediaQuery,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';

const STANDARDS_BY_TRACK_STRINGS = appStrings.modules.assessMyTrack.standardsByTrack;
const STANDARDS_BY_LEVEL_STRINGS = appStrings.modules.assessMyTrack.standardsByLevel;

const LEVEL_LABELS: Record<CriteriaLevel, string> = STANDARDS_BY_LEVEL_STRINGS.levelLabels;

const EMPTY_CELL = '-';

type VariantKind = StandardsVariant['kind'];

type StandardsByTrackModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  category: TrackAssessmentCategory;
  variant: StandardsVariant;
}>;

type TableModel = Readonly<{
  rowKeys: string[];
  columnKeys: string[];
  getCellContent: (rowKey: string, columnKey: string) => string;
}>;

type ColumnHeadingProps = Readonly<{
  columnKey: string;
  kind: VariantKind;
}>;

type StandardsColumnProps = Readonly<{
  columnKey: string;
  kind: VariantKind;
  showDivider: boolean;
  onColumnClick?: (() => void) | undefined;
  showDropdownIcon?: boolean;
  isMobile: boolean;
}>;

type StandardsTableProps = Readonly<{
  model: TableModel;
  kind: VariantKind;
  isMobile: boolean;
  selectedColumnIndex: number;
  onColumnSelect: (event: SelectChangeEvent<number>) => void;
}>;

const unique = (values: string[]): string[] => Array.from(new Set(values));

// zero-width space so slash-joined names ("Community/Impact") can wrap in a narrow cell
const wrapAtSlashes = (text: string): string => text.replace(/\//g, '/\u200B');

const byTrackModel = (standards: Standard[]): TableModel => ({
  rowKeys: unique(standards.map((s) => s.dimension_name)),
  columnKeys: unique(
    standards.map((s) => s.careerTrack?.name).filter((name): name is string => Boolean(name)),
  ),
  getCellContent: (rowKey, columnKey) =>
    standards.find((s) => s.dimension_name === rowKey && s.careerTrack?.name === columnKey)
      ?.standard_text || EMPTY_CELL,
});

const byLevelModel = (criteria: TrackAssessmentDimCriteria[]): TableModel => ({
  rowKeys: unique(
    [...criteria]
      .sort((a, b) => (a.dimension?.display_order ?? 0) - (b.dimension?.display_order ?? 0))
      .map((c) => c.dimension?.name)
      .filter((name): name is string => Boolean(name)),
  ),
  columnKeys: CRITERIA_LEVELS.filter((level) => criteria.some((c) => c.level === level)),
  getCellContent: (rowKey, columnKey) =>
    criteria.find((c) => c.dimension?.name === rowKey && c.level === columnKey)?.criteria_text ||
    EMPTY_CELL,
});

const getTableModel = (variant: StandardsVariant): TableModel =>
  variant.kind === 'by-level' ? byLevelModel(variant.criteria) : byTrackModel(variant.standards);

const ColumnHeading = ({ columnKey, kind }: ColumnHeadingProps) => {
  if (kind === 'by-level') {
    const level = columnKey as CriteriaLevel;

    return <DimensionScorePill score={CRITERIA_LEVEL_SCORES[level]} label={LEVEL_LABELS[level]} />;
  }

  return (
    <>
      <CareerTrackRadio careerTrack={columnKey} />
      <Typography variant="text-xs-semibold" sx={columnNameTextSx}>
        {`${columnKey} ${STANDARDS_BY_TRACK_STRINGS.track}`}
      </Typography>
    </>
  );
};

const StandardsColumn = ({
  columnKey,
  kind,
  showDivider,
  onColumnClick,
  showDropdownIcon,
  isMobile,
}: StandardsColumnProps) => {
  return (
    <TableCell
      sx={isMobile ? mobileDataHeaderCellSx : headerCellSx(showDivider)}
      onClick={onColumnClick}
      style={onColumnClick ? { cursor: 'pointer' } : {}}
    >
      <Box sx={columnNameSx}>
        <ColumnHeading columnKey={columnKey} kind={kind} />
        {showDropdownIcon && <KeyboardArrowDown sx={mobileDropdownIconSx} />}
      </Box>
    </TableCell>
  );
};

const StandardsTable = ({
  model,
  kind,
  isMobile,
  selectedColumnIndex,
  onColumnSelect,
}: StandardsTableProps) => {
  const { rowKeys, columnKeys, getCellContent } = model;
  const currentColumnKey = isMobile ? columnKeys[selectedColumnIndex] : undefined;
  const columnsToRender = isMobile
    ? [currentColumnKey].filter((key): key is string => Boolean(key))
    : columnKeys;

  // levels read as a progression, so they get a rule between them; tracks are alternatives
  const dividesColumns = kind === 'by-level' && !isMobile;

  return (
    <TableContainer sx={isMobile ? mobileTableContainerSx : tableContainerSx}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={isMobile ? mobileDimensionHeaderCellSx : dimensionHeaderCellSx}>
              <Typography variant="text-xs-semibold" sx={columnNameTextSx}>
                {STANDARDS_BY_TRACK_STRINGS.dimension}
              </Typography>
            </TableCell>
            {isMobile ? (
              <TableCell sx={mobileDataHeaderCellSx}>
                {columnKeys.length > 1 ? (
                  <Select
                    id="standards-by-track-column-select"
                    value={selectedColumnIndex}
                    onChange={onColumnSelect}
                    displayEmpty
                    sx={mobileSelectSx}
                    renderValue={(value) => {
                      const selectedKey = columnKeys[value as number];

                      return (
                        <Box sx={columnNameSx}>
                          {selectedKey && <ColumnHeading columnKey={selectedKey} kind={kind} />}
                        </Box>
                      );
                    }}
                  >
                    {columnKeys.map((columnKey, index) => (
                      <MenuItem key={columnKey} value={index}>
                        <Box sx={columnNameSx}>
                          <ColumnHeading columnKey={columnKey} kind={kind} />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Box sx={columnNameSx}>
                    {currentColumnKey && <ColumnHeading columnKey={currentColumnKey} kind={kind} />}
                  </Box>
                )}
              </TableCell>
            ) : (
              columnsToRender.map((colKey, index) => (
                <StandardsColumn
                  key={colKey}
                  columnKey={colKey}
                  kind={kind}
                  showDivider={dividesColumns && index > 0}
                  onColumnClick={undefined}
                  showDropdownIcon={false}
                  isMobile={isMobile}
                />
              ))
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rowKeys.map((rowKey) => (
            <TableRow key={rowKey}>
              <TableCell sx={isMobile ? mobileDimensionCellSx : dimensionCellSx}>
                <Typography variant="text-sm-medium">{wrapAtSlashes(rowKey)}</Typography>
              </TableCell>
              {columnsToRender.map((colKey, index) => (
                <TableCell
                  key={colKey}
                  sx={isMobile ? mobileDataCellSx : dataCellSx(dividesColumns && index > 0)}
                >
                  <CriteriaText text={getCellContent(rowKey, colKey)} textSx={dataCellTextSx} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const StandardsByTrackModal = ({
  open,
  onClose,
  category,
  variant,
}: StandardsByTrackModalProps) => {
  const isMobile = useMediaQuery('(max-width: 700px)');
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(0);

  const isByLevel = variant.kind === 'by-level';
  const model = getTableModel(variant);
  const strings = isByLevel ? STANDARDS_BY_LEVEL_STRINGS : STANDARDS_BY_TRACK_STRINGS;

  const handleColumnSelect = (event: SelectChangeEvent<number>) => {
    setSelectedColumnIndex(event.target.value as number);
  };

  // reset selected column when modal opens or data changes
  useEffect(() => {
    setSelectedColumnIndex(0);
  }, [open, variant]);

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="lg"
      bodyContent={
        <Box>
          <Box sx={headerSx}>
            <Box sx={headerTitleSx}>
              <Typography variant="display-sm-semibold">{strings.title}</Typography>
              <Box sx={chipsSx}>
                <Chip label={category.name} sx={chipSx} />
                {variant.kind === 'by-level' && <Chip label={variant.gradeLabel} sx={chipSx} />}
              </Box>
            </Box>
            <Typography variant="text-sm-regular">{strings.description}</Typography>
          </Box>

          <StandardsTable
            model={model}
            kind={variant.kind}
            isMobile={isMobile}
            selectedColumnIndex={selectedColumnIndex}
            onColumnSelect={handleColumnSelect}
          />
        </Box>
      }
    />
  );
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-3xl'],
  mt: { xs: backrs.spacing['spacing-3xl'], md: 0 },
});

const headerTitleSx: SxProps<Theme> = ({ backrs }) => ({
  columnGap: backrs.spacing['spacing-2xl'],
  rowGap: backrs.spacing['spacing-sm'],
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { xs: 'flex-start', md: 'flex-end' },
  mb: { xs: backrs.spacing['spacing-sm'], md: 0 },
});

const tableContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: `calc(100% + ${backrs.spacing['spacing-3xl']} * 2)`,
  boxShadow: 'none',
  marginLeft: `-${backrs.spacing['spacing-3xl']}`,
  marginBottom: `-${backrs.spacing['spacing-3xl']}`,
  '& .MuiTable-root': {
    tableLayout: 'fixed',
  },
});

const headerCellSx =
  (showDivider: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    ...(showDivider && {
      borderLeft: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    }),
  });

const dimensionHeaderCellSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  width: '15%',
});

const dimensionCellSx: SxProps<Theme> = ({ backrs }) => ({
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  verticalAlign: 'top',
  width: '15%',
});

const dataCellSx =
  (showDivider: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    verticalAlign: 'top',
    ...(showDivider && {
      borderLeft: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    }),
  });

const columnNameSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  width: '100%',
  '& .MuiTypography-root': {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
});

const columnNameTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const dataCellTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const chipsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  maxWidth: '100%',
  gap: backrs.spacing['spacing-sm'],
});

const chipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  color: backrs.colors['Colors/Text/text-secondary'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  ...typography['text-sm-medium'],
});

const mobileTableContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  boxShadow: 'none',
  marginLeft: `-${backrs.spacing['spacing-3xl']}`,
  marginBottom: `-${backrs.spacing['spacing-3xl']}`,
  '& .MuiTable-root': {
    tableLayout: 'fixed',
  },
});

const mobileDimensionHeaderCellSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  width: '40%',
  padding: backrs.spacing['spacing-md'],
});

const mobileDataHeaderCellSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  width: '60%',
  padding: backrs.spacing['spacing-md'],
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  },
});

const mobileDropdownIconSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  fontSize: '1rem',
});

const mobileDimensionCellSx: SxProps<Theme> = ({ backrs }) => ({
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  verticalAlign: 'top',
  width: '40%',
  padding: backrs.spacing['spacing-md'],
});

const mobileDataCellSx: SxProps<Theme> = ({ backrs }) => ({
  verticalAlign: 'top',
  width: '60%',
  padding: backrs.spacing['spacing-md'],
});

const mobileSelectSx: SxProps<Theme> = {
  width: '100%',
  '& .MuiSelect-select': {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
};
